import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
} from "react";
import { AuthContext, AuthContextValue } from "../contexts/AuthContext";
import { maakStempel, metStempel } from "../utils/notitieStempel";

interface Opties {
  /** Uit = geen stempel; het veld gedraagt zich als voorheen. */
  actief: boolean;
  value: string;
  onChange: (value: string) => void;
  /** Past het stempel niet meer binnen de limiet, dan blijft het weg. */
  maxLength?: number;
}

// Buiten de AuthProvider (en in tests die de context wegmocken) is er geen
// gebruiker en dus geen stempel.
const LegeContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * B9 (fixronde 12-10-2026): zet bij de eerste focus het notitiestempel in
 * een textarea, met de cursor erachter. Eén keer per leven van het veld;
 * `herstel()` maakt een nieuwe eerste focus mogelijk (bijv. nadat een
 * notitie geplaatst is en het veld weer leeg is).
 */
export function useNotitieStempel(
  ref: React.RefObject<HTMLTextAreaElement | null>,
  { actief, value, onChange, maxLength }: Opties,
) {
  const auth = useContext(AuthContext ?? LegeContext);
  const gestempeld = useRef(false);
  const cursorNaRender = useRef<number | null>(null);

  useLayoutEffect(() => {
    const cursor = cursorNaRender.current;
    const veld = ref.current;
    if (cursor === null || !veld) return;
    cursorNaRender.current = null;
    veld.setSelectionRange(cursor, cursor);
    // Bij een muisklik zet de browser de caret ná het focus-event op de
    // klikplek (vóór het stempel); één tik later zetten wij hem alsnog erachter
    // (feedback kantoor 1-11-2026).
    const timer = setTimeout(() => veld.setSelectionRange(cursor, cursor), 0);
    return () => clearTimeout(timer);
  }, [value, ref]);

  const naam = auth?.user?.naam || auth?.user?.afkorting || "";

  const onFocus = () => {
    if (!actief || gestempeld.current || !naam) return;
    const { tekst, cursor } = metStempel(value, maakStempel(naam));
    if (maxLength !== undefined && tekst.length > maxLength) return;
    gestempeld.current = true;
    cursorNaRender.current = cursor;
    onChange(tekst);
  };

  const herstel = () => {
    gestempeld.current = false;
  };

  return { onFocus, herstel };
}
